import React from 'react';
import ConfirmDialog from './ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  /** Plain text renders as a paragraph. Pass JSX for rich, structured bodies. */
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  /** Use `lg` or `xl` when the body carries a list/breakdown so it has room. */
  size?: 'md' | 'lg' | 'xl';
}

interface PendingState {
  open: boolean;
  opts: ConfirmOptions;
}

/**
 * Imperative in-app replacement for `window.confirm`. Returns:
 *   - `askConfirm(opts)` → Promise<boolean> that resolves true on confirm
 *     and false on cancel (button, X, backdrop, ESC).
 *   - `confirmDialog` — a React node to mount once per consumer so the
 *     dialog has a place to render.
 *
 * Callers can chain: `if (!(await askConfirm({ title, message }))) return;`
 * mirroring the `window.confirm` control flow without blocking the thread.
 */
export function useConfirm(): {
  askConfirm: (opts: ConfirmOptions) => Promise<boolean>;
  confirmDialog: React.ReactNode;
} {
  const [state, setState] = React.useState<PendingState>({
    open: false,
    opts: { title: '', message: '' },
  });

  // Ref so the second call from ConfirmDialog (onConfirm → onClose) is
  // idempotent — we null it out after the first resolve.
  const resolveRef = React.useRef<((v: boolean) => void) | null>(null);

  const askConfirm = React.useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const handleConfirm = React.useCallback(() => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setState((s) => ({ ...s, open: false }));
    r?.(true);
  }, []);

  const handleCancel = React.useCallback(() => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setState((s) => ({ ...s, open: false }));
    r?.(false);
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      isOpen={state.open}
      onClose={handleCancel}
      onConfirm={handleConfirm}
      title={state.opts.title}
      message={state.opts.message}
      confirmText={state.opts.confirmText}
      cancelText={state.opts.cancelText}
      variant={state.opts.variant}
      size={state.opts.size}
    />
  );

  return { askConfirm, confirmDialog };
}

export default useConfirm;
